const DEFAULT_OPTIONS = {
  maxAttempts: 3,
  msBetweenAttempts: 2000,
  timeMultiplier: 2
};

type AsyncRetryOptions = {
  maxAttempts?: number;
  msBetweenAttempts?: number;
  timeMultiplier?: number;
  onError?: (error: unknown) => void;
};

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const asyncRetry = async <T>(
  fn: () => Promise<T>,
  opts: AsyncRetryOptions = DEFAULT_OPTIONS
) => {
  const {
    maxAttempts = DEFAULT_OPTIONS.maxAttempts,
    msBetweenAttempts = DEFAULT_OPTIONS.msBetweenAttempts,
    timeMultiplier = DEFAULT_OPTIONS.timeMultiplier,
    onError = null
  } = opts;

  for (let i = 0; i < maxAttempts; i++) {
    try {
      return await fn();
    } catch (e) {
      if (i === maxAttempts - 1) {
        if (onError) {
          onError(e);
        } else {
          throw e;
        }
      }

      const multiplier = i === 0 ? 1 : i * timeMultiplier;
      await sleep(msBetweenAttempts * multiplier);
    }
  }

  throw new Error("Retry failed");
};

export const isEnumValue = <T extends object>(
  value: unknown,
  enumObject: T
): value is T[keyof T] => {
  return Object.values(enumObject).includes(value);
};

export const stringToEnum = <T extends object>(
  value: string | undefined | null,
  enumObject: T,
  defaultValue: T[keyof T]
): T[keyof T] => (isEnumValue(value, enumObject) ? value : defaultValue);
